import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import ImageGalleryEditor from "../../components/ImageGalleryEditor";
import CategorySelectWithAdd from "../../components/CategorySelectWithAdd";
import AIProductExtractPanel from "../../components/AIProductExtractPanel";
import { Plus, Trash2, Pencil, X, Sparkles, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import BulkEditBanner, { useBulkEdits, BulkEditToggle } from "../../components/BulkEditBanner";

const empty = {
    name: "", category_id: "", short_description: "", description: "", indications: "",
    composition: "", dosage: "", images: [], variants: [], active: true, featured: false,
};

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [writingCopy, setWritingCopy] = useState(false);

    const { setCell, cellValue, dirtyIds, dirtyPatches, reset: resetBulk } = useBulkEdits(items);

    // Min sellable price across a product's variants — used to sort the list
    // from cheapest to most expensive so "from ₹X" reads low → high top-down.
    // Products with no priced variants sort to the bottom.
    const productsSorted = useMemo(() => {
        const minPrice = (p) => {
            const prices = (p.variants || []).map((v) => Number(v.price) || 0).filter((n) => n > 0);
            return prices.length === 0 ? Number.POSITIVE_INFINITY : Math.min(...prices);
        };
        return [...items].sort((a, b) => minPrice(a) - minPrice(b));
    }, [items]);

    const load = async () => {
        const [p, c, s] = await Promise.all([
            api.get("/products", { params: { active_only: false } }),
            api.get("/categories", { params: { active_only: false } }),
            api.get("/sizes", { params: { active_only: false } }),
        ]);
        setItems(p.data); setCategories(c.data); setSizes(s.data);
    };
    useEffect(() => { load(); }, []);

    const reloadCategories = async () => {
        // Invoked from <CategorySelectWithAdd onCategoriesChanged> after the
        // user clicks "Create category" — not from a useEffect.
        const c = await api.get("/categories", { params: { active_only: false } });
        setCategories(c.data);
    };

    const startNew = () => { setEditing("new"); setForm({ ...empty, category_id: categories[0]?.id || "" }); };
    const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };

    /** Resolve an AI-suggested category name to an existing category_id, or
     *  create the category on the fly. Same behaviour as Bulk add (AI) so the
     *  two paths stay in lock-step. */
    const ensureCategoryId = async (suggested) => {
        const cleaned = (suggested || "").trim();
        if (!cleaned) return categories[0]?.id || "";
        const match = categories.find((c) => c.name.toLowerCase() === cleaned.toLowerCase());
        if (match) return match.id;
        try {
            const { data } = await api.post("/categories", { name: cleaned, slug: "" });
            setCategories((prev) => prev.some((c) => c.id === data.id) ? prev : [...prev, data]);
            return data.id;
        } catch {
            toast.message(`Couldn't create category "${cleaned}" — using "${categories[0]?.name || "first"}"`);
            return categories[0]?.id || "";
        }
    };

    /** Drop AI-extracted fields into the editor form. Empty values are kept
     *  blank rather than overwriting fields the admin already typed manually.
     *  Photos picked in the AI panel are appended to form.images so the
     *  product saves WITH a thumbnail (no "grey square" surprise on the list). */
    const applyExtracted = async (ex) => {
        if (!ex) return;
        const catId = await ensureCategoryId(ex.category);
        const newUrls = Array.isArray(ex._imageUrls) ? ex._imageUrls : [];
        setForm((prev) => ({
            ...prev,
            name: ex.name || prev.name,
            category_id: catId || prev.category_id,
            composition: ex.composition || prev.composition,
            dosage: ex.dosage || prev.dosage,
            indications: ex.indications || prev.indications,
            // Append (don't replace) — keeps any photos the admin already added.
            images: [...(prev.images || []), ...newUrls],
        }));
    };

    /** Generate marketing copy (short_description + description + tagline)
     *  from the facts the admin has already entered. Replaces the short and
     *  full description regardless of prior content — the assumption is that
     *  the admin pressed the button intentionally. Tagline is appended at the
     *  top of the long description so it shows up on the product page. */
    const aiWriteCopy = async () => {
        if (!form.name) {
            toast.error("Type a product name first — the AI needs something to anchor on.");
            return;
        }
        const catName = (categories.find((c) => c.id === form.category_id) || {}).name || "";
        setWritingCopy(true);
        try {
            const { data } = await api.post("/admin/products/ai-write-description", {
                name: form.name,
                composition: form.composition || "",
                indications: form.indications || "",
                dosage: form.dosage || "",
                category: catName,
            }, { timeout: 60000 });
            const tagline = (data.tagline || "").trim();
            const longCopy = tagline ? `${tagline}\n\n${data.description || ""}` : (data.description || "");
            setForm((prev) => ({
                ...prev,
                short_description: data.short_description || prev.short_description,
                description: longCopy || prev.description,
            }));
            toast.success(`AI wrote ${data.tagline ? "tagline + " : ""}description${data.notes ? ` · ${data.notes}` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI write failed");
        } finally {
            setWritingCopy(false);
        }
    };

    const save = async () => {
        if (!form.name) return toast.error("Name required");
        if (!form.category_id) return toast.error("Category required");
        if (form.variants.length === 0) return toast.error("Add at least one size variant");
        try {
            if (editing === "new") await api.post("/products", form);
            else await api.put(`/products/${editing}`, form);
            toast.success("Saved"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete product?")) return;
        await api.delete(`/products/${id}`); toast.success("Deleted"); load();
    };

    const addVariant = (sizeId) => {
        const sz = sizes.find(s => s.id === sizeId);
        if (!sz) return;
        if (form.variants.find(v => v.size_id === sizeId)) return toast.error("Already added");
        setForm({ ...form, variants: [...form.variants, { size_id: sz.id, size_label: sz.label, price: 0, stock: 0 }] });
    };
    const updateVariant = (idx, patch) => {
        const copy = [...form.variants];
        copy[idx] = { ...copy[idx], ...patch };
        setForm({ ...form, variants: copy });
    };
    const removeVariant = (idx) => setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) });

    /** Bulk save — PUT each dirty product with merged payload. Only changes name/category/active/featured. */
    const saveBulk = async () => {
        const patches = dirtyPatches();
        if (patches.length === 0) return;
        setBulkBusy(true);
        let ok = 0, fail = 0;
        for (const { id, original, patch } of patches) {
            if (!original) continue;
            try {
                const payload = {
                    name: patch.name ?? original.name,
                    category_id: patch.category_id ?? original.category_id,
                    short_description: original.short_description || "",
                    description: original.description || "",
                    indications: original.indications || "",
                    composition: original.composition || "",
                    dosage: original.dosage || "",
                    images: original.images || [],
                    variants: original.variants || [],
                    active: patch.active !== undefined ? !!patch.active : !!original.active,
                    featured: patch.featured !== undefined ? !!patch.featured : !!original.featured,
                };
                await api.put(`/products/${id}`, payload);
                ok += 1;
            } catch (e) {
                fail += 1;
                toast.error(`${original.name}: ${e.response?.data?.detail || "Save failed"}`);
            }
        }
        setBulkBusy(false);
        if (ok > 0) toast.success(`Saved · ${ok} product${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
        resetBulk();
        await load();
    };

    return (
        <div data-testid="admin-products">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                <div>
                    <div className="label-eyebrow text-sky-700">Catalog</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Products</h1>
                </div>
                <div className="flex gap-2">
                    <BulkEditToggle
                        active={bulkMode}
                        onClick={() => { setBulkMode(!bulkMode); resetBulk(); }}
                        testId="toggle-bulk-mode"
                    />
                    <Button onClick={startNew} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-product"><Plus className="w-4 h-4 mr-2" />New product</Button>
                </div>
            </div>

            {bulkMode && (
                <BulkEditBanner
                    dirtyCount={dirtyIds.length}
                    busy={bulkBusy}
                    onReset={resetBulk}
                    onSave={saveBulk}
                    helperText="Edit name, category, active and featured inline on the list below. Variants and images stay in the full editor."
                    testIdPrefix="bulk"
                />
            )}

            {editing && (
                <div className="bg-white border border-stone-200 p-6 mb-8 space-y-4">
                    <div className="flex items-center justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} product</h2>
                        <Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>

                    <AIProductExtractPanel
                        defaultName={form.name}
                        onExtracted={applyExtracted}
                        testId="prod-ai"
                    />

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className="rounded-none border-stone-300" data-testid="prod-name" />
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Category *</Label>
                            <CategorySelectWithAdd
                                value={form.category_id}
                                onChange={(v) => setForm({ ...form, category_id: v })}
                                categories={categories}
                                onCategoriesChanged={reloadCategories}
                                testId="prod-category"
                            />
                        </div>
                    </div>
                    <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} placeholder="Short description" className="rounded-none border-stone-300" data-testid="prod-short-desc" />
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Full description" className="rounded-none border-stone-300" data-testid="prod-desc" />
                    <Textarea value={form.indications} onChange={(e) => setForm({ ...form, indications: e.target.value })} placeholder="Indications (symptoms it helps)" className="rounded-none border-stone-300" data-testid="prod-indications" />
                    <Input value={form.composition} onChange={(e) => setForm({ ...form, composition: e.target.value })} placeholder="Composition" className="rounded-none border-stone-300" data-testid="prod-composition" />
                    <Input value={form.dosage || ""} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder="Dosage" className="rounded-none border-stone-300" data-testid="prod-dosage" />

                    {/* AI write description — pulls facts from the form above
                        and asks Claude to draft tagline + short + long copy. */}
                    <div className="border border-emerald-200 bg-emerald-50/40 p-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs text-stone-700 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-700" />
                            <span>
                                <strong>AI write description</strong> · drafts a tagline, short summary, and full marketing copy from the facts above. Edit afterwards as needed.
                            </span>
                        </div>
                        <Button
                            type="button"
                            onClick={aiWriteCopy}
                            disabled={writingCopy || !form.name}
                            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-stone-50"
                            data-testid="prod-ai-write"
                        >
                            {writingCopy
                                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Writing…</>
                                : <><Sparkles className="w-4 h-4 mr-1" />Write copy with AI</>}
                        </Button>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Images</Label>
                        <div className="mt-2">
                            <ImageGalleryEditor
                                value={form.images}
                                onChange={(images) => setForm({ ...form, images })}
                                testId="prod-images"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Size variants &amp; prices</Label>
                        <p className="text-[11px] text-stone-500 mb-3">Each size can carry its <strong>own photos</strong> (e.g. the 30ml bottle vs the 450ml box). The product gallery, indications, composition and dosage stay shared across all sizes.</p>
                        <div className="flex gap-2 items-center mb-3">
                            <Select onValueChange={addVariant}>
                                <SelectTrigger className="rounded-none border-stone-300 w-48" data-testid="add-variant-trigger"><SelectValue placeholder="+ Add a size" /></SelectTrigger>
                                <SelectContent>{sizes.filter(s => s.active).map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-3">
                            {form.variants.map((v, idx) => (
                                <div key={v.size_id} className="border border-stone-200 bg-stone-50/40 p-3 space-y-3">
                                    <div className="grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-2 font-mono text-sm font-medium">{v.size_label}</div>
                                        <Input type="number" value={v.price} onChange={(e) => updateVariant(idx, { price: parseFloat(e.target.value || 0) })} placeholder="Price" className="rounded-none border-stone-300 col-span-3" data-testid={`variant-price-${v.size_label}`} />
                                        <Input type="number" value={v.stock} onChange={(e) => updateVariant(idx, { stock: parseInt(e.target.value || 0) })} placeholder="Stock" className="rounded-none border-stone-300 col-span-3" />
                                        <Input value={v.sku || ""} onChange={(e) => updateVariant(idx, { sku: e.target.value })} placeholder="SKU (optional)" className="rounded-none border-stone-300 col-span-3" />
                                        <Button variant="ghost" onClick={() => removeVariant(idx)} className="col-span-1 text-red-700 rounded-none" title={`Remove ${v.size_label}`}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                    <div className="pl-2 border-l-2 border-sky-200">
                                        <Label className="text-[10px] uppercase tracking-widest text-sky-700 mb-1 block">Photos for {v.size_label}</Label>
                                        <p className="text-[11px] text-stone-500 mb-2">Leave blank to use the shared product gallery above. Add photos here to show <strong>only</strong> when {v.size_label} is selected.</p>
                                        <ImageGalleryEditor
                                            value={v.images || []}
                                            onChange={(images) => updateVariant(idx, { images })}
                                            testId={`variant-images-${v.size_label}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></label>
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Featured on home</span><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /></label>
                    </div>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full" data-testid="prod-save">Save product</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200">
                {productsSorted.map((p) => {
                    const isEdited = dirtyIds.includes(p.id);
                    const liveName = cellValue(p.id, "name", p.name);
                    const liveCat = cellValue(p.id, "category_id", p.category_id);
                    const liveActive = cellValue(p.id, "active", !!p.active);
                    const liveFeatured = cellValue(p.id, "featured", !!p.featured);
                    return (
                        <div key={p.id} className={`border-b border-stone-100 last:border-b-0 p-4 flex items-center gap-4 ${isEdited ? "bg-amber-50/40" : ""}`} data-testid={`prod-row-${p.slug}`}>
                            <div className="w-12 h-12 bg-stone-100 shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                            <div className="flex-1 min-w-0">
                                {bulkMode ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            value={liveName}
                                            onChange={(e) => setCell(p.id, "name", e.target.value, p.name)}
                                            className={`rounded-none h-8 text-sm font-medium ${liveName !== p.name ? "border-amber-500 bg-amber-50" : "border-stone-200"}`}
                                            data-testid={`bulk-name-${p.id}`}
                                        />
                                        <Select value={liveCat} onValueChange={(v) => setCell(p.id, "category_id", v, p.category_id)}>
                                            <SelectTrigger className={`rounded-none h-8 text-sm ${liveCat !== p.category_id ? "border-amber-500 bg-amber-50" : "border-stone-200"}`} data-testid={`bulk-cat-${p.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                ) : (
                                    <>
                                        <div className="font-medium">{p.name} {!p.active && <span className="text-xs uppercase text-stone-400 ml-2">hidden</span>} {p.featured && <span className="text-xs uppercase text-sky-700 ml-2">featured</span>}</div>
                                        <div className="text-xs text-stone-500">{p.variants?.length || 0} sizes · {p.variants?.[0] ? `from ${formatINR(p.variants[0].price)}` : "—"}</div>
                                    </>
                                )}
                            </div>
                            {bulkMode && (
                                <>
                                    <label className="flex items-center gap-1.5 text-xs text-stone-600">
                                        <Switch checked={!!liveActive} onCheckedChange={(v) => setCell(p.id, "active", v, !!p.active)} data-testid={`bulk-active-${p.id}`} />
                                        <span>Active</span>
                                    </label>
                                    <label className="flex items-center gap-1.5 text-xs text-stone-600">
                                        <Switch checked={!!liveFeatured} onCheckedChange={(v) => setCell(p.id, "featured", v, !!p.featured)} data-testid={`bulk-featured-${p.id}`} />
                                        <span>Featured</span>
                                    </label>
                                </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => startEdit(p)} className="rounded-none" title="Open full editor" data-testid={`prod-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => del(p.id)} className="rounded-none text-red-700"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                    );
                })}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No products yet.</div>}
            </div>
        </div>
    );
}
